import { formatDate, isClaimOrderLevel, isClaimItemLevel } from '@helpers';
import { DISPLAY_ORDER_RESOLUTIONS, SalesChannel } from '@pulse/constants/claims';
import get from 'lodash/get';
import filter from 'lodash/filter';
import map from 'lodash/map';
import { defineStore } from 'pinia';
import short from 'short-uuid';
import { computed, ref, reactive } from 'vue';

import useFileUploader from '@pulse/composables/useFileUploader';
import ClaimService from '@pulse/services/ClaimService';

import isEmpty from 'lodash/isEmpty';
import { SubmitClaimOptions } from '@pulse/types/claims';
import { useOrdersStore } from '@pulse/composables/useOrdersStore';
import { ensureUrlVersion } from '../../../helpers/url.helper';

export const DEFAULT_CLAIM = {
  id: '',
  category: null,
  customer: {},
  fulfillments: {},
  resolution: {
    desiredMethod: null,
    updatedShippingAddress: null,
  },
  signature: '',
};

export const useClaimStore = config => {
  if (isEmpty(config)) {
    throw new Error('Missing config. Pass `useRuntimeConfig()`');
  }

  const apiBaseUrl = ensureUrlVersion(config.public.apiBaseUrl);

  return defineStore('pulseClaim', () => {
    const state = reactive({
      claimSession: null,
    });
    // Had to change claim to a ref so that the subscription in claim-session.js plugin can detect changes with reactive it wasn't picking up every change
    const claim = ref(DEFAULT_CLAIM);
    const order = useOrdersStore(config)();
    const currentShipmentId = ref(null);
    const service = new ClaimService(apiBaseUrl);
    const { uploadFiles } = useFileUploader(config);
    const requireCustomerSignature = ref(false);
    const submitClaimOptions: SubmitClaimOptions = {
      fromValet: false,
      requireCustomerSignature: requireCustomerSignature.value,
    };

    async function findClaimById(id) {
      return service.findClaimById(id);
    }

    async function findClaimSession(orderId) {
      const session = await service.findClaimSession(orderId);
      state.claimSession = session;

      if (session.metadata?.claim) {
        setClaim(session.metadata.claim);
      }

      if (session.metadata?.currentShipmentId) {
        currentShipmentId.value = session.metadata.currentShipmentId;
      }

      return session;
    }

    async function updateClaimSession(data) {
      const updatedSession = await service.updateClaimSession(data);
      state.claimSession = updatedSession;

      if (updatedSession.metadata?.claim && data.metadata?.claim) {
        setClaim(data.metadata.claim);
      }

      if (updatedSession.metadata?.currentShipmentId && data.metadata?.currentShipmentId) {
        currentShipmentId.value = data.metadata.currentShipmentId;
      }

      return updatedSession;
    }

    async function findReviews(claimId) {
      return service.findReviews(claimId);
    }

    async function createReview(review, claimId) {
      return service.createReview(review, claimId);
    }

    async function deleteClaimSession() {
      if (!state.claimSession) return;

      await service.deleteClaimSession(state.claimSession.id);
      state.claimSession = null;
      claim.value = DEFAULT_CLAIM;
      currentShipmentId.value = null;
    }

    async function sendClaimMessage(content, sourceOrderId, customerId) {
      let medias: unknown[] = [];

      if (content.files.length > 0) {
        medias = await uploadFiles(content.files);
      }
      const storeId = claim.value._data.storeId;
      const claimId = claim.value._data.id;

      const claimMessageInput = {
        claimId,
        medias,
        messageContent: content.messageContent,
        senderId: customerId,
        senderName: claim.value.customer.customerName,
        senderType: 'customer',
        storeId,
      };

      await createClaimMessage(claimMessageInput, storeId, sourceOrderId);

      const newMessages = await loadClaimMessages(claimId, sourceOrderId);

      claim.value.messages = newMessages;
    }

    async function createClaimMessage(data, storeId, sourceOrderId) {
      return service.createClaimMessage(data, storeId, sourceOrderId);
    }

    async function loadClaimMessages(claimId, sourceOrderId) {
      return service.loadClaimMessages(claimId, sourceOrderId);
    }

    function addFulfillmentIssues(id, issues) {
      const issuesWithIds = issues.map(issue => ({ ...issue, id: short.generate() }));

      if (!claim.value.fulfillments[id]) {
        claim.value.fulfillments[id] = {
          id: id,
          issues: issuesWithIds,
        };
      } else {
        /**
         * Remove the issues for the sourceItemId that are already in the store.
         */
        claim.value.fulfillments[id].issues = filter(
          claim.value.fulfillments[id].issues,
          ({ sourceItemId }) => !map(issues, 'sourceItemId').includes(sourceItemId),
        );

        /**
         * Add the new issues for that sourceItemId to the store.
         */
        claim.value.fulfillments[id].issues = claim.value.fulfillments[id].issues.concat(issuesWithIds);
      }
    }

    function setClaim(claimData) {
      claim.value = {
        ...claim.value,
        ...claimData,
      };
    }

    /**
     * @param {Array.<string>} fulfillments
     * sets the fulfillment object in the store
     * this allows for the routing object to loop through the fulfillments with issues
     *
     * @returns {void}
     */
    function setFulfillmentState(fulfillments) {
      fulfillments.forEach(id => {
        claim.value.fulfillments[id] = {
          id,
          issues: [],
        };
      });
    }

    /**
     * @param {string} id
     * sets the current shipment id, this is used to keep track of the current shipment issues.
     *
     * @returns {void}
     */
    function setCurrentShipmentId(id) {
      currentShipmentId.value = id;
    }

    /**
     * Sets the desired resolution for the claim.
     * @enum {string} resolution
     */
    function setRequestedResolution(resolution) {
      claim.value.resolution.desiredMethod = resolution;
    }

    /**
     * Sets the category of a claim.
     * @enum {string} type
     */
    function setClaimCategory(category) {
      claim.value.category = category;
    }

    /**
     * setShippingAddress
     * updates the claim.resolution.updatedShippingAddress with the provided address
     * @param {Object} address
     */
    function setShippingAddress(address) {
      claim.value.resolution.updatedShippingAddress = address;
    }

    /**
     * Sets the whole shipment issue. This allows us to persist data between the shipment type and the details
     * We have to set details as well so that we can loop through and add each item with the same issue
     * @param {string} id
     * @param {string} type
     */
    function setShipmentIssueType(id, type) {
      const fulfillment = claim.value.fulfillments[id];

      claim.value.fulfillments[id] = {
        ...fulfillment,
        shipmentIssue: type,
      };
    }

    /**
     * Sets the details for a shipment issue
     * this is global and is separate from the issues. This is just for data persistence
     * @param {string} id
     * @param {string} details
     */
    function setShipmentIssueDetails(id, details) {
      claim.value.fulfillments[id].details = details;
    }

    function addSignature(imageUrl: string) {
      claim.value.signature = imageUrl;
    }

    function saveSignature() {
      service.saveCustomerSignature(claim.value.id, claim.value.signature);
    }

    function deleteIssueByFulfillmentAndIssue(fulfillmentId, issueId) {
      claim.value.fulfillments[fulfillmentId].issues = claim.value.fulfillments[fulfillmentId].issues.filter(
        ({ id }) => id !== issueId,
      );
    }

    function clearIssuesByFulfillment(fulfillmentId) {
      if (claim.value.fulfillments[fulfillmentId]) {
        claim.value.fulfillments[fulfillmentId].issues = [];
      }
    }

    function getIssuesByFulfillmentAndProduct(fulfillmentId, sourceItemId) {
      return get(claim.value.fulfillments, [fulfillmentId, 'issues'], []).filter(
        ({ sourceItemId: id }) => id === sourceItemId,
      );
    }

    function transformStoreToClaim(email, claimInfo) {
      const [firstFulfillment] = Object.values(claimInfo.fulfillments);
      const [firstIssue] = firstFulfillment?.issues || [];

      return {
        claimCategory: claim.value.category,
        email,
        fulfillmentIssues: Object.entries(claimInfo.fulfillments).reduce(
          (acc: { issues: any; sourceFulfillmentId: string }[], [sourceFulfillmentId, fulfillment]) => {
            acc.push({
              issues: fulfillment.issues.map(({ item, ...issue }) => ({
                ...issue,
              })),
              sourceFulfillmentId,
            });

            return acc;
          },
          [],
        ),
        resolution: {
          customerSignature: claimInfo.signature,
          desiredMethod: claimInfo.resolution.desiredMethod,
          updatedShippingAddress: claimInfo.resolution.updatedShippingAddress,
          warrantyPeriod: order.currentOrder?.eligibility?.[firstIssue?.sourceItemId]?.warranty?.validity ?? null,
        },
        salesChannel: SalesChannel.ONLINE,
      };
    }

    function submitClaim(options = submitClaimOptions) {
      const createClaimData: any = {
        claim: transformStoreToClaim(claim.value.customer.email, claim.value),
        sourceOrderId: claim.value.customer.sourceOrderId,
        ...claim.value.customer,
      };

      if (options.fromValet) {
        return service.createClaimFromValet(createClaimData, options);
      }

      return service.createClaim(createClaimData);
    }

    function notifyCustomer(claimId: string) {
      return service.notifyClaimCustomer(claimId);
    }

    function setCustomer({ email, sourceOrderId, customerName }) {
      const customer = {
        email,
        sourceOrderId,
        customerName,
      };

      claim.value = { ...DEFAULT_CLAIM, ...claim.value, customer };
    }

    function reset() {
      claim.value.fulfillments = {};
      claim.value.resolution.updatedShippingAddress = null;
      claim.value.signature = null;
    }

    /**
     * @typedef {Object} FoundationFulfillment
     * @property {string} id
     * @property {Array.<Object>} issues
     *
     * @returns {Array.<FoundationFulfillment>}
     */
    const unfinishedFulfillments = computed(() => {
      return Object.values(claim.value.fulfillments).filter(({ issues }) => issues.length === 0);
    });

    const isOrderLevel = computed(() => {
      return isClaimOrderLevel(claim.value);
    });

    const isItemLevel = computed(() => {
      return isClaimItemLevel(claim.value);
    });

    const claimCategory = computed(() => {
      return claim.value.category;
    });

    const messages = computed(() => {
      if (!claim?.value?._data?.claimMessages) {
        return [];
      }

      return claim?.value?._data?.claimMessages.map(message => {
        return {
          ...message,
          date: formatDate(message.createdAt),
          // image: 'https://picsum.photos/200',
          key: message.id,
          medias: message.medias || [],
          message: message.messageContent,
          senderType: message.senderType,
          type: 'message',
          username: message.senderName,
        };
      });
    });

    const resolution = computed(() => {
      return claim.value.resolution ? claim.value.resolution : null;
    });

    const currentShipment = computed(() => {
      return claim.value.fulfillments[currentShipmentId.value];
    });

    const currentShipmentIssues = computed(() => {
      return currentShipment.value?.issues || [];
    });

    const allIssues = computed(() => {
      return Object.values(claim.value.fulfillments).reduce((all, { issues }) => all.concat(issues), []);
    });

    const requestedResolution = computed(() => DISPLAY_ORDER_RESOLUTIONS[claim.value.resolution.desiredMethod]);

    const claimSession = computed(() => state.claimSession);

    return {
      addFulfillmentIssues,
      addSignature,
      allIssues,
      claim,
      claimCategory,
      claimSession,
      clearIssuesByFulfillment,
      createClaimMessage,
      createReview,
      currentShipment,
      currentShipmentId,
      currentShipmentIssues,
      deleteClaimSession,
      deleteIssueByFulfillmentAndIssue,
      findClaimById,
      findClaimSession,
      findReviews,
      getIssuesByFulfillmentAndProduct,
      isItemLevel,
      isOrderLevel,
      loadClaimMessages,
      messages,
      notifyCustomer,
      requestedResolution,
      reset,
      resolution,
      requireCustomerSignature,
      saveSignature,
      sendClaimMessage,
      setClaim,
      setClaimCategory,
      setCurrentShipmentId,
      setCustomer,
      setFulfillmentState,
      setRequestedResolution,
      setShipmentIssueDetails,
      setShipmentIssueType,
      setShippingAddress,
      submitClaim,
      unfinishedFulfillments,
      updateClaimSession,
    };
  });
};
